import clsx from 'clsx'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import RecentSearchItem from '@/components/Search/RecentSearchItem'
import SearchResultsPanel from '@/components/Search/SearchResultsPanel'
import getRoutes from '@/lib/getRoutes'
import { fetchRest } from '@/lib/hook/tanstackQuery'
import pushRoute from '@/lib/pushRoute'
import { buildHighlightResult } from '@/lib/search/highlight'
import { ApiQueryEndpoint } from '@/src/ApiQueryEndpoint'

// Autocomplete Elasticsearch.
// UI via le composant SearchResultsPanel
// (sections, styles, surlignage <mark>) — seules les donnees different :
// elles viennent de l'endpoint REST CS-Cart `search_suggest` (via le proxy
// /api/csCartProxy).

// Des la 1re lettre : les marques seules (classees par la boutique, les plus commandees
// d'abord). A partir de 3 caracteres : produits, marques et suggestions.
const MIN_BRAND_QUERY_LENGTH = 1
const MIN_QUERY_LENGTH = 3
const BRANDS_ONLY_LIMIT = 6
const DEBOUNCE_MS = 250
const RECENT_LIMIT = 10
// Cle localStorage historique : les recherches recentes deja enregistrees restent lues
// (key 'recent_search_autocomplete') : l'historique existant est conserve au switch.
const RECENT_STORAGE_KEY = 'AUTOCOMPLETE_RECENT_SEARCHES:recent_search_autocomplete'

type SuggestProduct = {
  product_id: string | number
  product: string
  seo_name: string
  product_code?: string
  brand_name?: string
  brand_seo_name?: string
  brand_variant_id?: string | number
  company_name?: string
  company_seo_name?: string
  main_pair?: unknown
  price?: number
  discount?: number
  precommande?: string
  avail_since?: number
}

type SuggestBrand = {
  name: string
  seo_name: string
  variant_id?: string | number | null
  count: number
  label: string
}

type SuggestResponse = {
  queries?: { query: string }[]
  products?: SuggestProduct[]
  brands?: SuggestBrand[]
}

type RecentSearch = { id: string; label: string }

type NavItem =
  | { type: 'recent'; label: string }
  | { type: 'query'; label: string }
  | { type: 'brand'; brand: SuggestBrand }
  | { type: 'product'; product: SuggestProduct }

const readRecentSearches = (): RecentSearch[] => {
  if (typeof window === 'undefined') return []
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_STORAGE_KEY) || '[]')
    if (!Array.isArray(raw)) return []
    return raw
      .map(item => ({
        id: String(item?.id ?? item?.label ?? ''),
        label: String(item?.label ?? item?.id ?? ''),
      }))
      .filter(item => item.label !== '')
      .slice(0, RECENT_LIMIT)
  } catch {
    return []
  }
}

const writeRecentSearches = (searches: RecentSearch[]) => {
  try {
    localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(searches.slice(0, RECENT_LIMIT)))
  } catch {
    // stockage indisponible (mode prive...) : historique non persiste, sans erreur
  }
}

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 640px)')
    const onChange = () => setIsMobile(mql.matches)
    onChange()
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  return isMobile
}

const EsAutocomplete: React.FC = () => {
  const router = useRouter()
  const isMobile = useIsMobile()

  const inputRef = useRef<HTMLInputElement | null>(null)
  const requestIdRef = useRef(0)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SuggestResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([])

  useEffect(() => {
    setRecentSearches(readRecentSearches())
  }, [])

  // Fermeture au changement de page
  useEffect(() => {
    setOpen(false)
  }, [router.asPath])

  const saveRecentSearch = useCallback((label: string) => {
    const trimmed = label.trim()
    if (!trimmed) return
    setRecentSearches(previous => {
      const next = [
        { id: trimmed, label: trimmed },
        ...previous.filter(item => item.label.toLowerCase() !== trimmed.toLowerCase()),
      ].slice(0, RECENT_LIMIT)
      writeRecentSearches(next)
      return next
    })
  }, [])

  const removeRecentSearch = useCallback((label: string) => {
    setRecentSearches(previous => {
      const next = previous.filter(item => item.label !== label)
      writeRecentSearches(next)
      return next
    })
  }, [])

  const submitSearch = useCallback(
    (value: string) => {
      const trimmed = value.trim()
      if (!trimmed) return
      saveRecentSearch(trimmed)
      setOpen(false)
      pushRoute(
        getRoutes({
          route: 'Catalogsearch',
          argsObject: { query: trimmed },
        })
      )
    },
    [saveRecentSearch]
  )

  const fetchSuggestions = useCallback((value: string) => {
    const requestId = ++requestIdRef.current
    const brandsOnly = value.length < MIN_QUERY_LENGTH
    setLoading(true)
    fetchRest<SuggestResponse>({
      endpoint: ApiQueryEndpoint.CATALOG.SEARCH_SUGGEST.endpoint,
      queriesParams: brandsOnly
        ? { q: value, limit_products: 0, limit_brands: BRANDS_ONLY_LIMIT }
        : { q: value, limit_products: 5, limit_brands: 4 },
    })
      .then(data => {
        if (requestId !== requestIdRef.current) return
        setResults({
          queries: Array.isArray(data?.queries) ? data.queries : [],
          products: Array.isArray(data?.products) ? data.products : [],
          brands: Array.isArray(data?.brands) ? data.brands : [],
        })
      })
      .catch(() => {
        // vide silencieux : pas de resultats plutot qu'une erreur bloquante
        if (requestId !== requestIdRef.current) return
        setResults({ queries: [], products: [], brands: [] })
      })
      .finally(() => {
        if (requestId === requestIdRef.current) setLoading(false)
      })
  }, [])

  const hideLeftColumn =
    typeof window !== 'undefined' && window.location.pathname.includes('/customer/basket')
  // Le panier masque la colonne des marques : les marques seules n'y auraient rien a montrer
  const minLength = hideLeftColumn ? MIN_QUERY_LENGTH : MIN_BRAND_QUERY_LENGTH

  const handleChange = (value: string) => {
    setQuery(value)
    setActiveIndex(-1)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (value.trim().length < minLength) {
      requestIdRef.current++
      setResults(null)
      setLoading(false)
      return
    }
    setOpen(true)
    debounceRef.current = setTimeout(() => fetchSuggestions(value.trim()), DEBOUNCE_MS)
  }

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), [])

  const queryLength = query.trim().length
  const hasQueryResults = queryLength >= minLength && results !== null
  // Une ou deux lettres : marques seules (une reponse arrivee pour une saisie plus longue est ignoree)
  const brandsOnly = queryLength < MIN_QUERY_LENGTH
  const products = hasQueryResults && !brandsOnly ? (results?.products ?? []) : []
  const brands = hasQueryResults ? (results?.brands ?? []) : []
  const queries = hasQueryResults && !brandsOnly ? (results?.queries ?? []) : []

  // Recherches recentes filtrees par la saisie (facon Google) : affichees en
  // dropdown sous la barre de recherche, seules celles qui contiennent le
  // texte tape restent visibles (toutes si la saisie est vide).
  const filteredRecents = useMemo(() => {
    const needle = query.trim().toLowerCase()
    const matching = needle
      ? recentSearches.filter(item => item.label.toLowerCase().includes(needle))
      : recentSearches
    return matching.slice(0, RECENT_LIMIT)
  }, [recentSearches, query])

  // Surlignage des termes de la recherche : _highlightResult consomme par ProductCard/Highlight
  const highlightedProducts = useMemo(
    () =>
      products.map(product => ({
        ...product,
        _highlightResult:
          buildHighlightResult(query, product, ['product', 'product_code', 'brand_name']) ??
          undefined,
      })),
    [products, query]
  )

  // Liste plate ordonnee pour la navigation clavier — meme ordre visuel :
  // recentes (dropdown sous la barre) > marques > suggestions > produits
  const navItems: NavItem[] = useMemo(
    () => [
      ...filteredRecents.map(item => ({ type: 'recent' as const, label: item.label })),
      ...brands.map(brand => ({ type: 'brand' as const, brand })),
      ...queries.map(item => ({ type: 'query' as const, label: item.query })),
      ...products.map(product => ({ type: 'product' as const, product })),
    ],
    [filteredRecents, queries, brands, products]
  )

  const navId = (item: NavItem): string => {
    if (item.type === 'recent') return `recent:${item.label}`
    if (item.type === 'query') return `query:${item.label}`
    if (item.type === 'brand') return `brand:${item.brand.name}`
    return `product:${item.product.product_id}`
  }

  const selectNavItem = (item: NavItem) => {
    if (item.type === 'recent' || item.type === 'query') {
      setQuery(item.label)
      submitSearch(item.label)
      return
    }
    if (item.type === 'brand') {
      setOpen(false)
      pushRoute(getRoutes({ route: 'Brand', values: { '[slug]': item.brand.seo_name } }))
      return
    }
    setOpen(false)
    pushRoute(getRoutes({ route: 'Product', values: { '[slug]': item.product.seo_name } }))
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false)
      inputRef.current?.blur()
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      if (open && activeIndex >= 0 && navItems[activeIndex]) {
        selectNavItem(navItems[activeIndex])
      } else {
        submitSearch(query)
      }
      return
    }
    if (!open || navItems.length === 0) return
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex(previous => (previous + 1) % navItems.length)
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex(previous => (previous <= 0 ? navItems.length - 1 : previous - 1))
    }
  }

  const handleClose = () => setOpen(false)

  // Panneau plein ecran : des la 1re lettre (marques seules), complet a partir de 3 caracteres.
  // Le dropdown des recentes, lui, vit sous la barre des le focus.
  const showPanel = open && (hasQueryResults || loading)
  const showRecentDropdown = open && filteredRecents.length > 0
  const showOverlay = showPanel || showRecentDropdown

  const activeId = activeIndex >= 0 && navItems[activeIndex] ? navId(navItems[activeIndex]) : null
  const handleHoverItem = (id: string) =>
    setActiveIndex(navItems.findIndex(item => navId(item) === id))

  return (
    <>
      {showOverlay && <div className="fixed inset-0 bg-black/60 z-30" onClick={handleClose} />}

      <div
        id="searchBox-container"
        data-open={showOverlay}
        className={clsx(
          'h-fit left-0 right-0',
          showOverlay && 'fixed inset-0 z-40 overflow-hidden'
        )}
      >
        {/* Classes aa-* du theme d'autocompletion
            (autocomplete-theme.css + autocomplete.css) : .aa-Autocomplete (bande blanche
            quand data-open, police), .aa-Form (barre gradient, largeur),
            .aa-Input (taille/couleur du texte et du placeholder), icones du
            theme (aa-SubmitIcon / aa-ClearIcon). */}
        <div className="aa-Autocomplete">
          <form
            role="search"
            className="aa-Form"
            onSubmit={event => {
              event.preventDefault()
              submitSearch(query)
            }}
          >
            <div className="aa-InputWrapperPrefix">
              <label className="aa-Label" aria-label="Rechercher">
                <button className="aa-SubmitButton" type="submit" title="Rechercher">
                  <svg
                    className="aa-SubmitIcon"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M16.041 15.856c-0.034 0.026-0.067 0.055-0.099 0.087s-0.060 0.064-0.087 0.099c-1.258 1.213-2.969 1.958-4.855 1.958-1.933 0-3.682-0.782-4.95-2.050s-2.050-3.017-2.050-4.95 0.782-3.682 2.050-4.95 3.017-2.050 4.95-2.050 3.682 0.782 4.95 2.050 2.050 3.017 2.050 4.95c0 1.886-0.745 3.597-1.959 4.856zM21.707 20.293l-3.675-3.675c1.231-1.54 1.968-3.493 1.968-5.618 0-2.485-1.008-4.736-2.636-6.364s-3.879-2.636-6.364-2.636-4.736 1.008-6.364 2.636-2.636 3.879-2.636 6.364 1.008 4.736 2.636 6.364 3.879 2.636 6.364 2.636c2.125 0 4.078-0.737 5.618-1.968l3.675 3.675c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414z" />
                  </svg>
                </button>
              </label>
            </div>
            <div className="aa-InputWrapper">
              <input
                ref={inputRef}
                type="search"
                value={query}
                placeholder="Recherchez une marque, un produit, EAN…"
                className="aa-Input"
                autoComplete="off"
                spellCheck={false}
                onChange={event => handleChange(event.target.value)}
                onFocus={() => setOpen(true)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div className="aa-InputWrapperSuffix">
              {query !== '' && (
                <button
                  className={clsx('aa-ClearButton', isMobile && 'hidden')}
                  type="button"
                  title="Effacer"
                  onClick={() => {
                    handleChange('')
                    inputRef.current?.focus()
                  }}
                >
                  <svg
                    className="aa-ClearIcon"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="currentColor"
                    aria-hidden
                  >
                    <path d="M5.293 6.707l5.293 5.293-5.293 5.293c-0.391 0.391-0.391 1.024 0 1.414s1.024 0.391 1.414 0l5.293-5.293 5.293 5.293c0.391 0.391 1.024 0.391 1.414 0s0.391-1.024 0-1.414l-5.293-5.293 5.293-5.293c0.391-0.391 0.391-1.024 0-1.414s-1.024-0.391-1.414 0l-5.293 5.293-5.293-5.293c-0.391-0.391-1.024-0.391-1.414 0s-0.391 1.024 0 1.414z" />
                  </svg>
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {showOverlay && (
        <div
          className="fixed top-0 right-0 md:right-8 h-16 cursor-pointer z-40"
          onClick={handleClose}
        >
          <div className="flex items-center justify-center h-full pr-4">
            <span className="bg-gray-100 rounded-full text-black w-8 h-8 flex items-center justify-center">
              ✕
            </span>
          </div>
        </div>
      )}

      {/* Recherches recentes facon Google : dropdown sous la barre, filtre par
          la saisie, au-dessus du panneau resultats (z-60 > z-50) */}
      {showRecentDropdown && (
        <div className="fixed top-[58px] left-0 right-0 z-[60]">
          <div
            className="mx-auto w-full max-w-[800px] bg-white rounded-b-2xl shadow-lg py-1"
            onMouseLeave={() => setActiveIndex(-1)}
          >
            {filteredRecents.map(item => {
              const id = `recent:${item.label}`
              return (
                <div
                  key={item.label}
                  className={clsx('px-2', id === activeId && 'bg-gray-100')}
                  onMouseEnter={() => handleHoverItem(id)}
                >
                  <RecentSearchItem
                    hit={{
                      label: item.label,
                      onTapAhead: () => selectNavItem({ type: 'recent', label: item.label }),
                      onRemove: () => removeRecentSearch(item.label),
                    }}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}

      {showPanel && (
        <div
          id="searchBox"
          className="fixed top-16 bg-white rounded-b-lg z-50 left-0 right-0"
          onMouseLeave={() => setActiveIndex(-1)}
        >
          <SearchResultsPanel
            isMobile={isMobile}
            loading={loading}
            hideLeftColumn={hideLeftColumn}
            brands={brands.map(brand => ({ id: `brand:${brand.name}`, label: brand.label }))}
            suggestions={queries.map(item => ({
              id: `query:${item.query}`,
              label: item.query,
              onSelect: () => selectNavItem({ type: 'query', label: item.query }),
            }))}
            products={highlightedProducts}
            productsPlaceholder={
              brandsOnly ? (
                <div className="aa-SourceNoResults">{t({ id: 'web_saisie_min_produits' })}</div>
              ) : undefined
            }
            onSubmit={() => submitSearch(query)}
            activeId={activeId}
            onHoverItem={handleHoverItem}
          />
        </div>
      )}
    </>
  )
}

export default EsAutocomplete
